import React, { useState, useMemo } from "react";
import { Tooltip, Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { theme as colorTheme } from "../../../../Convoze.theme";

const CustomerFrequency = ({ cardDetails }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const navigate = useNavigate();

  const names = cardDetails?.FrequentKeywordData?.names || [];
  const values = cardDetails?.FrequentKeywordData?.values || [];
  const list = names.map((n, i) => ({
    label: n.replace(/\s*\(\d+x\)/, "").trim(),
    count: values[i] || 0,
  }));

  const HoverTooltipBox = ({ label, count }) => (
    <Box display="flex" flexDirection="column" gap={0.5} sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ fontSize: "14px", color: "#555" }}>
        Word Frequency
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: colorTheme("palette", "positive").value,
          }}
        />
        <Typography
          variant="body2"
          sx={{ fontSize: "13px", color: "#555", fontWeight: 600 }}
        >
          {label?.replace(/\s*\(\d+x\)/, "").trim()}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 900, fontSize: "13px", marginLeft: "auto" }}
        >
          {count}
        </Typography>
      </Box>
    </Box>
  );

  // ✅ Function to scale frequency
  function transformFrequencyData(list) {
    const max = Math.max(...list.map((d) => d.count), 0);
    return list.map(({ label, count }) => {
      if (max === 0) return { label, count, value: 0, category: "Frequency" };
      const scaledValue = Math.log(count + 1) / Math.log(max + 1);
      return {
        label,
        count,
        value: (scaledValue * 100).toFixed(2),
        category: "Frequency",
      };
    });
  }

  const transformedData = transformFrequencyData(list);
  const isEmpty = !transformedData.length;

  // 🎨 Define color palette
  const colorPalette = [
    colorTheme("palette", "primary").value,
    colorTheme("palette", "positive").value,
    colorTheme("palette", "neutral").value,
    colorTheme("palette", "negative").value,
    "#6366F1", // custom purple
  ];

  // ✅ Generate fixed random colors once
  const barColors = useMemo(() => {
    const getRandomColor = () =>
      colorPalette[Math.floor(Math.random() * colorPalette.length)];
    return transformedData.map(() => getRandomColor());
  }, [transformedData.length]); // run once based on data length

  return (
    <Box
      sx={{
        flex: 1,
        borderRadius: 2,
        height: "50vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box width="100%" display="flex" justifyContent="space-between" mb="20px">
        <Typography
          fontSize={14}
          variant="body2"
          color="#464646"
          fontWeight={700}
        >
          Customer Word Frequency
        </Typography>
      </Box>

      {isEmpty ? (
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="1rem"
          fontWeight={600}
          color="#999"
          borderRadius="8px"
        >
          No data available
        </Box>
      ) : (
        <Box
          flex={1}
          overflow="auto"
          pr="18px"
          sx={{
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: " #C1C7CE",
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              bgcolor: "#C1C7CE",
            },
          }}
        >
          {transformedData?.map((item, index) => (
            <Tooltip
              key={index}
              title={<HoverTooltipBox label={item.label} count={item.count} />}
              placement="top"
              arrow
              componentsProps={{
                tooltip: { sx: { bgcolor: "#fff", color: "#333", p: 2 } },
              }}
            >
              <Box
                mb={0.25}
                px={1.25}
                py={1.25}
                sx={{
                  cursor: "pointer",
                  backgroundColor: hoveredIndex === index ? "#E6E6E6" : "",
                  borderRadius: hoveredIndex === index ? "5px" : 0,
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() =>
                  navigate("/user/audio-details", {
                    state: {
                      label: item?.label.replace(/\s*\(\d+x\)/, "").trim(),
                      source: "dashboard-frequency",
                    },
                  })
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.6,
                  }}
                >
                  <Box fontSize={14} fontWeight={700}>
                    {item?.label?.replace(/\s*\(\d+x\)/, "").trim()}
                  </Box>
                  <Box textAlign="right" fontSize={14} fontWeight={900}>
                    {item?.count}
                  </Box>
                </Box>
                <Box
                  flex={1}
                  bgcolor="#e0e0e0"
                  borderRadius="10px"
                  height="10px"
                  overflow="hidden"
                >
                  <Box
                    width={`${item?.value}%`}
                    bgcolor={barColors[index]} // 🎨 fixed random color
                    height="100%"
                    borderRadius="10px"
                    sx={{
                      minWidth: item.value > 0 ? "5%" : 0,
                      transition: "width 0.4s ease",
                    }}
                  />
                </Box>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CustomerFrequency;
